import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import DevExpress from 'devextreme/bundles/dx.all';
import CustomStore from 'devextreme/data/custom_store';
import notify from 'devextreme/ui/notify';
import isNil from 'lodash-es/isNil';
import moment, { Moment, utc } from 'moment';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { oc } from 'ts-optchain';
import { DxContextMenuPreparingEvent, DxToolbarPreparingEvent } from '../../../../core/dx.types';
import { hAll } from '../../../../shared/classes/utils/utils';
import { CommonService } from '../../../../shared/modules/my-common/services/common.service';
import { ConfigService } from '../../../../shared/modules/my-common/services/config.service';
import { DataSourceService } from '../../../../shared/modules/my-common/services/datasource.service';
import { PusherService } from '../../../../shared/modules/my-common/services/pusher.service';
import { StateStoreService } from '../../../../shared/modules/my-common/services/state-store.service';
import { ABaseComponent } from '../../../../shared/modules/ui/components/abstract/a-base.component';
import { GridHelperService } from '../../../../shared/modules/ui/services/grid-helper.service';
import { UiService } from '../../../../shared/modules/ui/services/ui.service';
import { Consumer, ConsumerApi, LoggerService, Person, Program } from '../../../../shared/sdk';
import { CONSUMER_STATUSES } from '../../classes/enums';
import { HelperService } from '../../services/helper.service';
import { ConsumerFormComponent } from '../consumer-form/consumer-form.component';
import DataSourceOptions = DevExpress.data.DataSourceOptions;

@Component({
  selector: 'app-eligibility-grid',
  templateUrl: './eligibility-grid.component.html',
  styleUrls: ['./eligibility-grid.component.scss'],
})
export class EligibilityGridComponent extends ABaseComponent implements OnInit, OnDestroy {
  constructor(
    public logger: LoggerService,
    public common: CommonService,
    public config: ConfigService,
    private pusher: PusherService,
    private ui: UiService,
    private dss: DataSourceService,
    private sss: StateStoreService,
    public helper: HelperService,
    private gridHelper: GridHelperService,
    protected dialog: MatDialog,
  ) {
    super(logger);

    const startOfYear = utc().startOf('y').utc();
    const dates: Moment[] = [];

    const curMonth = moment().startOf('month');
    const fromMonth = curMonth.clone().subtract(1, 'year').startOf('month');

    for (const m = fromMonth.clone(); curMonth.diff(m, 'month') >= 0; m.add(1, 'month')) {
      dates.push(m.clone());
    }
    this.dates = dates;

    this.grid_stateStoring = {
      enabled: true,
      type: 'localStorage',
      storageKey: '66c2c0d6-162f-42e7-b1ee-ee2c225b874a',
    };

    this.dso$ = this.buildDataSource();

    const store = this.dss.getStore(Program);
    this.programDso = {
      store,
      sort: [{ selector: 'name' }],
    };
  }

  get now() {
    return moment().format('YYYY_MM_DD');
  }

  dso$: Observable<DataSourceOptions> = of([]);
  programDso: DataSourceOptions = [];
  statuses = CONSUMER_STATUSES;

  grid_stateStoring: any;
  modifiedSubscription: Subscription;

  dates: Moment[];
  mciMap: Map<string, any> = new Map();

  @ViewChild(DxDataGridComponent, { static: true }) grid: DxDataGridComponent;

  $facilityName$ = new BehaviorSubject('FACILITY');

  ngOnInit() {
    this.modifiedSubscription = this.dss.modifiedEvent.subscribe(modelName => {
      if ([Consumer.getModelName(), Person.getModelName()].includes(modelName)) {
        if (this.grid) {
          this.grid.instance.refresh();
        }
      }
    });

    this.config.tenant$
      .pipe(
        map(f => (f.name || 'FACILITY').replace(' ', '_')),
        tap(name => {
          if (this.$facilityName$.value !== name) {
            this.$facilityName$.next(name);
          }
        }),
        takeUntil(this.$onDestroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.modifiedSubscription.unsubscribe();
  }

  grid_onInitialized(e) {
    this.gridHelper.handle(e.component, {
      notifyErrors: true,
    });
  }

  grid_onToolbarPreparing(e: DxToolbarPreparingEvent) {
    if (this.config.roles.includes('SU')) {
      e.toolbarOptions.items.unshift({
        widget: 'dxDropDownButton',
        name: 'suProcedures',
        locateInMenu: 'auto',
        location: 'after',
        showText: 'always',
        options: {
          icon: 'fas fa-super-user',
          text: 'SU Actions',
          items: [
            {
              text: 'Stedi: Request E9ies',
              onClick: () => {
                this.callRpc('STEDI_E9Y_BATCH_PROCESS', {
                  yyyymmdd: [moment().format('YYYYMM') + '01'],
                  useRunTask: true,
                  queue: 'stedi-batch-queue',
                });
              },
            },
            {
              text: 'Stedi: Check Prev Month Shifts E9ies',
              onClick: () => {
                this.callRpc('STEDI_E9Y_CHECK_DURING_MONTH_SHIFT', {
                  yyyymmdd: [moment().subtract(1, 'month').format('YYYYMM') + '01'],
                  useRunTask: true,
                });
              },
            },
            {
              text: 'Stedi: Poll E9ies',
              onClick: () => {
                this.callRpc('STEDI_E9Y_BATCH_POLL', { useRunTask: true });
              },
            },
            {
              text: 'Stedi: Store E9ies to Clients',
              onClick: () => {
                this.callRpc('STEDI_E9Y_STORE_TO_CLIENTS', {
                  yyyymmdd: [moment().format('YYYYMM') + '01'],
                  useRunTask: true,
                });
              },
            },
          ],
        },
      });
    }
  }

  grid_onContextMenuPreparing(e: DxContextMenuPreparingEvent) {
    e.items = [];

    if (this.config.roles.includes('SU') && e.row && !e.row.isEditing) {
      if (e.row.rowType === 'data') {
        e.items.push(
          {
            text: `Gainwell: Request E9ies for ${oc(e).row.data.person_firstname()} ${oc(e).row.data.person_lastname()}`,
            onItemClick: () => {
              void this.dss.getApi<ConsumerApi>(Consumer).requestEdiEligibilities(e.row.data.id, hAll).toPromise();
            },
          },
          {
            text: `Gainwell: Store E9ies to ${oc(e).row.data.person_firstname()} ${oc(e).row.data.person_lastname()}`,
            onItemClick: () => {
              void this.dss.getApi<ConsumerApi>(Consumer).updateEdiEligibilities(e.row.data.id, hAll).toPromise();
            },
          },
        );
      }

      //
      let yymmm = e.column.caption.split(' ')[0];
      yymmm = moment(yymmm, 'YY-MMM', true).isValid() ? yymmm : undefined;
      if (!!yymmm) {
        if (e.row.rowType === 'header') {
          e.items.push(
            {
              text: `Gainwell: Request E9y for ${yymmm}`,
              onItemClick: () => {
                this.callRpc('requestBbsEligibility', {
                  yyyymm: [Number(moment(yymmm + '-01', 'YY-MMM-DD').format('YYYYMM'))],
                  useRunUrgent: true,
                });
              },
            },
            {
              text: `Gainwell: Store E9y for ${yymmm}`,
              onItemClick: () => {
                this.callRpc('storeEdiEligibilityToClientBatch', {
                  yyyymm: [Number(moment(yymmm + '-01', 'YY-MMM-DD').format('YYYYMM'))],
                  useRunTask: true,
                });
              },
            },
          );
        } else if (e.row.rowType === 'data') {
          // this.logger.log(e);
          e.items.push(
            {
              text: `Stedi: Request E9y for ${oc(e).row.data.person_firstname()} ${oc(e).row.data.person_lastname()}`,
              onItemClick: () => {
                this.callRpc('STEDI_E9Y_BATCH_PROCESS', {
                  yyyymmdd: [moment(yymmm + '-01', 'YY-MMM-DD').format('YYYYMM') + '01'],
                  mciArr: [e.row.data.mci],
                  useRunTask: true,
                  queue: 'stedi-batch-queue',
                });
              },
            },
            {
              text: `Stedi: Store E9y to ${oc(e).row.data.person_firstname()} ${oc(e).row.data.person_lastname()}`,
              onItemClick: () => {
                this.callRpc('STEDI_E9Y_STORE_TO_CLIENTS', {
                  yyyymmdd: [moment(yymmm + '-01', 'YY-MMM-DD').format('YYYYMM') + '01'],
                  mciArr: [e.row.data.mci],
                  useRunTask: true,
                });
              },
            },
          );
        }
      }
    }
  }

  grid_onEditingStart(e: any): void {
    e.cancel = true;

    if (this.common.auth.getCurrentTenant() !== e.data.facility_id) {
      notify('You are not allowed to edit this client', 'error', 5000);
      return;
    }

    const subTitle = [
      moment.utc(e.data.person_dob).isValid()
        ? 'DOB:' + moment.utc(e.data.person_dob).utc().format('MM/DD/YYYY')
        : undefined,
      e.data.mci ? 'MCI:' + e.data.mci : undefined,
      e.data.status ? e.data.status : undefined,
      // e.data.activeMco ? e.data.activeMco : undefined,
      // e.data.activeBroker ? e.data.activeBroker : undefined,
    ]
      .filter(i => !isNil(i))
      .join(' - ');
    const title = `Edit: ${this.helper.displayExpr(e.data)}${subTitle.length ? ' - ' + subTitle : ''}`;

    this.ui.openEditDialog({
      modelId: e.key,
      ModelClass: Consumer,
      FormComponentClass: ConsumerFormComponent,
      title,
    });
  }

  mcoDataField(date: Moment) {
    return 'eligibility.' + date.format('YYYY-MM');
  }

  mcoSortField(date) {
    return 'eligibility.\\"' + date.format('YYYY-MM') + '\\".value';
  }

  private callRpc(
    job: string,
    params: { [key: string]: any } & ({ useRunJob: boolean } | { useRunTask: boolean } | { useRunUrgent: boolean }),
  ) {
    this.ui.showLoading();
    return this.pusher
      .rpc(job, params, hAll)
      .pipe(
        catchError(err => of(notify(err.message, 'error', 5000))),
        tap(() => this.ui.hideLoading()),
        takeUntil(this.$onDestroy$),
      )
      .subscribe();
  }

  private buildDataSource() {
    return this.config.tenantType$.pipe(
      withLatestFrom(this.config.isSU$),
      map(([type, isSU]) => {
        const so = this.dss.getStoreOptions(Consumer, undefined, false);

        // show others tenants clients for SU
        if (type === 'BASE' && isSU) {
          so.customHeaders = hAll;
        }

        return {
          store: new CustomStore(so),
        } as DataSourceOptions;
      }),
    );

    // dso.postProcess = (data: Array<any>): Array<any> => {
    //
    //   data.forEach((c) => {
    //     Object.values(c.eligibility || {}).forEach((e: any) => {
    //       e._value = e.mco || e.status || e.error;
    //     });
    //   });
    //
    //   return data;
    // };
  }
}
